import { useState, useEffect, useMemo, useDeferredValue, useRef, useCallback } from "react";
import { SEO } from "../components/SEO";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X, Grid, List, Loader2, Star, ArrowUpDown, Users, Palette, Car } from "lucide-react";
import CarCard from "../components/CarCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import odooApi from "../services/odooApi";
import { useLanguage } from "../contexts/LanguageContext";
import { useCurrency } from "../contexts/CurrencyContext";

// Available seat options
const SEAT_OPTIONS = [2, 4, 5, 6, 7, 8];

// Available colors
const COLOR_OPTIONS = [
  { name: "White", value: "white", hex: "#FFFFFF" },
  { name: "Black", value: "black", hex: "#000000" },
  { name: "Silver", value: "silver", hex: "#C0C0C0" },
  { name: "Grey", value: "grey", hex: "#808080" },
  { name: "Red", value: "red", hex: "#FF0000" },
  { name: "Blue", value: "blue", hex: "#0000FF" },
  { name: "Green", value: "green", hex: "#008000" },
  { name: "Yellow", value: "yellow", hex: "#FFD700" },
  { name: "Orange", value: "orange", hex: "#FFA500" },
  { name: "Brown", value: "brown", hex: "#8B4513" },
];

export const ListingsPage = () => {
  const { t } = useLanguage();
  const { formatPrice } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [cars, setCars] = useState([]);
  const [allCars, setAllCars] = useState([]);
  const [viewMode, setViewMode] = useState("grid");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [metadataLoaded, setMetadataLoaded] = useState(false);
  const [totalCars, setTotalCars] = useState(0);

  // Dynamic filter options from API
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);

  // Filter states - initialized from URL params
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || searchParams.get("query") || searchParams.get("q") || "");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [sortBy, setSortBy] = useState("featured");
  const requestSeqRef = useRef(0);
  const lastTrackedSearchRef = useRef("");
  const searchSnapshot = searchParams.toString();
  const specialFilter = searchParams.get("filter");

  // Get max price from cars
  const maxPrice = useMemo(() => {
    if (allCars.length === 0) return 10000;
    return Math.max(...allCars.map(car => car.pricePerDay || 0), 0) || 10000;
  }, [allCars]);

  const hasCustomPriceRange = priceRange[0] > 0 || priceRange[1] < maxPrice;

  const buildBackendFilters = useCallback(() => {
    const params = { limit: 500, offset: 0, sort_by: sortBy };
    const query = deferredSearchQuery.trim();

    if (query) params.query = query;
    if (selectedBrands.length > 0) params.brand = selectedBrands.join(",");
    if (selectedCategories.length > 0) params.category = selectedCategories.join(",");
    if (selectedCities.length > 0) params.city = selectedCities.join(",");
    if (selectedSeats.length > 0) params.seats = selectedSeats.join(",");
    if (selectedColors.length > 0) params.color = selectedColors.join(",");
    if (priceRange[0] > 0) params.min_price = priceRange[0];
    if (priceRange[1] < maxPrice) params.max_price = priceRange[1];

    if (specialFilter) params.filter = specialFilter;

    return params;
  }, [
    deferredSearchQuery,
    maxPrice,
    priceRange,
    selectedBrands,
    selectedCategories,
    selectedCities,
    selectedColors,
    selectedSeats,
    sortBy,
    specialFilter,
  ]);

  // Fetch cars and filter options from Odoo
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [carsRes, catsRes, citiesRes, brandsRes] = await Promise.all([
          odooApi.getCars({ limit: 500, sort_by: "featured" }),
          odooApi.getCategories(),
          odooApi.getCities(),
          odooApi.getBrands(),
        ]);
        
        const carsData = carsRes.cars || carsRes;
        setAllCars(carsData);
        setCars(carsData);
        setTotalCars(carsRes.total || carsData.length);
        setCategoryOptions(catsRes.map(c => ({ name: c.name, slug: c.slug })));
        setCityOptions(citiesRes.map(c => ({ name: c.name, slug: c.slug })));
        setBrandOptions(brandsRes.map(b => ({ name: b.name, slug: b.slug })));

        // Set price range based on actual data
        const prices = carsData.map(c => c.pricePerDay || 0);
        if (prices.length > 0) {
          setPriceRange([0, Math.max(...prices)]);
        }
        setMetadataLoaded(true);
      } catch (err) {
        console.error("Failed to load listings:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    setSearchQuery(searchParams.get("search") || searchParams.get("query") || searchParams.get("q") || "");
  }, [searchParams]);

  useEffect(() => {
    const resolveParam = (options, paramName) => {
      const value = searchParams.get(paramName);
      if (!value) return [];
      const normalizedValue = value.toLowerCase();
      const match = options.find((option) =>
        option.slug === value ||
        option.name.toLowerCase() === normalizedValue ||
        option.slug.includes(normalizedValue) ||
        option.name.toLowerCase().includes(normalizedValue)
      );
      return [match?.name || value];
    };

    setSelectedBrands(resolveParam(brandOptions, "brand"));
    setSelectedCities(resolveParam(cityOptions, "city"));
    setSelectedCategories(resolveParam(categoryOptions, "category"));
  }, [searchParams, brandOptions, cityOptions, categoryOptions]);

  // Apply all listing filters on backend.
  useEffect(() => {
    if (!metadataLoaded) {
      return;
    }

    const requestId = ++requestSeqRef.current;
    const fetchFilteredCars = async () => {
      setLoading(true);
      try {
        const data = await odooApi.getCars(buildBackendFilters());
        if (requestId !== requestSeqRef.current) {
          return;
        }
        setCars(data.cars || []);
        setTotalCars(data.total || 0);
      } catch (err) {
        if (requestId !== requestSeqRef.current) {
          return;
        }
        console.error("Failed to load filtered listings:", err);
        setCars([]);
        setTotalCars(0);
      } finally {
        if (requestId === requestSeqRef.current) {
          setLoading(false);
        }
      }
    };

    fetchFilteredCars();
  }, [
    buildBackendFilters,
    deferredSearchQuery,
    metadataLoaded,
    maxPrice,
    priceRange,
    searchSnapshot,
    selectedBrands,
    selectedCategories,
    selectedCities,
    selectedColors,
    selectedSeats,
    sortBy,
  ]);

  useEffect(() => {
    if (loading) {
      return;
    }

    const query = deferredSearchQuery.trim();
    const filters = {};

    if (selectedCategories.length > 0) filters.categories = [...selectedCategories].sort();
    if (selectedCities.length > 0) filters.cities = [...selectedCities].sort();
    if (selectedBrands.length > 0) filters.brands = [...selectedBrands].sort();
    if (selectedSeats.length > 0) filters.seats = [...selectedSeats].sort((a, b) => a - b);
    if (selectedColors.length > 0) filters.colors = [...selectedColors].sort();
    if (priceRange[0] > 0) filters.min_price = priceRange[0];
    if (priceRange[1] < maxPrice) filters.max_price = priceRange[1];
    if (sortBy !== "featured") filters.sort_by = sortBy;
    if (specialFilter) filters.filter = specialFilter;

    const hasSearchIntent = Boolean(query) || Object.keys(filters).length > 0;
    if (!hasSearchIntent) {
      lastTrackedSearchRef.current = "";
      return;
    }

    const signature = JSON.stringify({ query, filters });
    if (lastTrackedSearchRef.current === signature) {
      return;
    }

    lastTrackedSearchRef.current = signature;
    odooApi.trackSearch(query || "filtered_browse", filters, `/cars${searchSnapshot ? `?${searchSnapshot}` : ""}`);
  }, [
    deferredSearchQuery,
    loading,
    maxPrice,
    priceRange,
    searchSnapshot,
    selectedBrands,
    selectedCategories,
    selectedCities,
    selectedColors,
    selectedSeats,
    specialFilter,
    sortBy,
  ]);

  // Toggle functions
  const toggleCategory = (category) => {
    setSelectedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };
  const toggleCity = (city) => {
    setSelectedCities((prev) =>
      prev.includes(city) ? prev.filter((c) => c !== city) : [...prev, city]
    );
  };
  const toggleBrand = (brand) => {
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
    );
  };
  const toggleSeats = (seats) => {
    setSelectedSeats((prev) =>
      prev.includes(seats) ? prev.filter((s) => s !== seats) : [...prev, seats]
    );
  };
  const toggleColor = (color) => {
    setSelectedColors((prev) =>
      prev.includes(color) ? prev.filter((c) => c !== color) : [...prev, color]
    );
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategories([]);
    setSelectedCities([]);
    setSelectedBrands([]);
    setSelectedSeats([]);
    setSelectedColors([]);
    setPriceRange([0, maxPrice]);
    setSortBy("featured");
    setSearchParams({});
  };

  const activeFiltersCount = 
    selectedCategories.length + 
    selectedCities.length + 
    selectedBrands.length + 
    selectedSeats.length + 
    selectedColors.length +
    (searchQuery ? 1 : 0) +
    (hasCustomPriceRange ? 1 : 0) +
    (specialFilter ? 1 : 0);

  const renderFilterContent = () => (
    <div className="space-y-6">
      {/* Search */}
      <div>
        <label className="text-sm font-medium text-gray-900 dark:text-white mb-3 block flex items-center gap-2">
          <Search className="w-4 h-4 text-[#D4AF37]" />
          {t("filter.search", "Search")}
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder={t("filter.searchPlaceholder", "Search cars...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Brand */}
      <div>
        <label className="text-sm font-medium text-gray-900 dark:text-white mb-3 block flex items-center gap-2">
          <Car className="w-4 h-4 text-[#D4AF37]" />
          {t("filter.brand", "Brand")}
        </label>
        <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
          {brandOptions.map((brand) => (
            <div key={brand.slug} className="flex items-center gap-2">
              <Checkbox
                id={`brand-${brand.slug}`}
                checked={selectedBrands.includes(brand.name) || selectedBrands.includes(brand.slug)}
                onCheckedChange={() => toggleBrand(brand.name)}
              />
              <label htmlFor={`brand-${brand.slug}`} className="text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
                {brand.name}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Category */}
      <div>
        <label className="text-sm font-medium text-gray-900 dark:text-white mb-3 block">
          {t("filter.category", "Category")}
        </label>
        <div className="space-y-2">
          {categoryOptions.map((category) => (
            <div key={category.slug} className="flex items-center gap-2">
              <Checkbox
                id={`cat-${category.slug}`}
                checked={selectedCategories.includes(category.name) || selectedCategories.includes(category.slug)}
                onCheckedChange={() => toggleCategory(category.name)}
              />
              <label htmlFor={`cat-${category.slug}`} className="text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
                {category.name}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Number of Seats */}
      <div>
        <label className="text-sm font-medium text-gray-900 dark:text-white mb-3 block flex items-center gap-2">
          <Users className="w-4 h-4 text-[#D4AF37]" />
          {t("filter.seats", "Number of Seats")}
        </label>
        <div className="flex flex-wrap gap-2">
          {SEAT_OPTIONS.map((seats) => (
            <button
              key={seats}
              onClick={() => toggleSeats(seats)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                selectedSeats.includes(seats)
                  ? "bg-[#D4AF37] text-black"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              {seats}
            </button>
          ))}
        </div>
      </div>

      {/* Color */}
      <div>
        <label className="text-sm font-medium text-gray-900 dark:text-white mb-3 block flex items-center gap-2">
          <Palette className="w-4 h-4 text-[#D4AF37]" />
          {t("filter.color", "Color")}
        </label>
        <div className="flex flex-wrap gap-2">
          {COLOR_OPTIONS.map((color) => (
            <button
              key={color.value}
              onClick={() => toggleColor(color.value)}
              className={`w-8 h-8 rounded-full border-2 transition-all ${
                selectedColors.includes(color.value)
                  ? "border-[#D4AF37] ring-2 ring-[#D4AF37]/30 scale-110"
                  : "border-gray-300 dark:border-gray-600 hover:border-gray-400"
              }`}
              style={{ backgroundColor: color.hex }}
              title={color.name}
            />
          ))}
        </div>
      </div>

      {/* City */}
      <div>
        <label className="text-sm font-medium text-gray-900 dark:text-white mb-3 block">
          {t("filter.city", "City")}
        </label>
        <div className="space-y-2">
          {cityOptions.map((city) => (
            <div key={city.slug} className="flex items-center gap-2">
              <Checkbox
                id={`city-${city.slug}`}
                checked={selectedCities.includes(city.name) || selectedCities.includes(city.slug)}
                onCheckedChange={() => toggleCity(city.name)}
              />
              <label htmlFor={`city-${city.slug}`} className="text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
                {city.name}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Price Range */}
      <div>
        <label className="text-sm font-medium text-gray-900 dark:text-white mb-3 block">
          {t("filter.priceRange", "Price Range")} ({t("common.perDay", "/day")})
        </label>
        <Slider
          value={priceRange}
          onValueChange={setPriceRange}
          min={0}
          max={maxPrice}
          step={50}
          className="my-4"
        />
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
          <span>{formatPrice(priceRange[0])}</span>
          <span>{formatPrice(priceRange[1])}</span>
        </div>
      </div>

      {/* Clear Filters Button */}
      <Button
        variant="outline"
        className="w-full border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
        onClick={clearFilters}
        data-testid="clear-filters"
      >
        {t("filter.clearAll", "Clear All Filters")}
      </Button>
    </div>
  );

  return (
    <>
      <SEO
        title={t("listings.pageTitle", "Rent Cars in UAE | Browse Luxury, Sports & Economy Cars")}
        description={t("listings.pageDescription", "Browse and rent luxury, sports, and economy cars in Dubai, Abu Dhabi, and across UAE. Compare prices from top suppliers and book instantly.")}
        keywords="rent car dubai, car rental uae, luxury car hire, sports car rental, suv rental dubai, economy car rental, car booking online"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Cars" },
        ]}
      />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-950" data-testid="listings-page">
        {/* Header */}
        <div className="bg-black py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="font-[var(--font-display)] text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t("listings.title", "Browse Our")} <span className="text-gold-gradient">{t("listings.titleHighlight", "Collection")}</span>
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              {t("listings.subtitle", "Discover the perfect vehicle for your journey from our extensive fleet")}
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-start gap-8">
            {/* Desktop Sidebar Filters */}
            <aside className="hidden lg:block w-72 shrink-0 self-start">
              <div className="sticky top-28 flex max-h-[calc(100vh-8rem)] flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="mb-6 flex items-center justify-between px-6 pt-6">
                  <h2 className="font-[var(--font-display)] text-xl font-semibold dark:text-white flex items-center gap-2">
                    <SlidersHorizontal className="w-5 h-5 text-[#D4AF37]" />
                    {t("filter.title", "Filters")}
                  </h2>
                  {activeFiltersCount > 0 && (
                    <span className="bg-[#D4AF37] text-black text-xs font-bold px-2 py-1 rounded-full">
                      {activeFiltersCount}
                    </span>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto overscroll-contain px-6 pb-6 pr-4">
                  {renderFilterContent()}
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1">
              {/* Controls Bar */}
              <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-6 bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-4">
                  {/* Mobile Filter Button */}
                  <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                    <SheetTrigger asChild>
                      <Button variant="outline" className="lg:hidden relative" data-testid="mobile-filter-btn">
                        <SlidersHorizontal className="w-4 h-4 mr-2" />
                        {t("filter.title", "Filters")}
                        {activeFiltersCount > 0 && (
                          <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-black text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                            {activeFiltersCount}
                          </span>
                        )}
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-80 overflow-y-auto">
                      <SheetHeader>
                        <SheetTitle className="flex items-center gap-2">
                          <SlidersHorizontal className="w-5 h-5 text-[#D4AF37]" />
                          {t("filter.title", "Filters")}
                        </SheetTitle>
                      </SheetHeader>
                      <div className="mt-6">{renderFilterContent()}</div>
                    </SheetContent>
                  </Sheet>

                  <span className="text-gray-600 dark:text-gray-400 text-sm">
                    {t("listings.showing", "Showing")} <strong className="text-gray-900 dark:text-white">{totalCars}</strong> {t("listings.vehicles", "vehicles")}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  {/* Sort Dropdown */}
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-48 border-gray-200 dark:border-gray-700" data-testid="sort-select">
                      <ArrowUpDown className="w-4 h-4 mr-2 text-gray-400" />
                      <SelectValue placeholder={t("sort.placeholder", "Sort by")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="featured">{t("sort.featured", "Featured")}</SelectItem>
                      <SelectItem value="rating">{t("sort.highestRated", "Highest Rated")}</SelectItem>
                      <SelectItem value="newest">{t("sort.newest", "Newest First")}</SelectItem>
                      <SelectItem value="price-low">{t("sort.priceLow", "Price: Low to High")}</SelectItem>
                      <SelectItem value="price-high">{t("sort.priceHigh", "Price: High to Low")}</SelectItem>
                      <SelectItem value="name-asc">{t("sort.nameAZ", "Name: A-Z")}</SelectItem>
                      <SelectItem value="name-desc">{t("sort.nameZA", "Name: Z-A")}</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* View Toggle */}
                  <div className="hidden sm:flex items-center border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <button
                      className={`p-2.5 transition-colors ${viewMode === "grid" ? "bg-[#D4AF37] text-black" : "text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"}`}
                      onClick={() => setViewMode("grid")}
                      data-testid="grid-view-btn"
                    >
                      <Grid className="w-4 h-4" />
                    </button>
                    <button
                      className={`p-2.5 transition-colors ${viewMode === "list" ? "bg-[#D4AF37] text-black" : "text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"}`}
                      onClick={() => setViewMode("list")}
                      data-testid="list-view-btn"
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Active Filters Tags */}
              {activeFiltersCount > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {searchQuery && (
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm">
                      "{searchQuery}"
                      <button onClick={() => setSearchQuery("")}><X className="w-3 h-3" /></button>
                    </span>
                  )}
                  {selectedBrands.map((brand) => (
                    <span key={brand} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-sm font-medium">
                      {brand}
                      <button onClick={() => toggleBrand(brand)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {selectedCategories.map((cat) => (
                    <span key={cat} className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm">
                      {cat}
                      <button onClick={() => toggleCategory(cat)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {selectedCities.map((city) => (
                    <span key={city} className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm">
                      {city}
                      <button onClick={() => toggleCity(city)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {selectedSeats.map((seats) => (
                    <span key={seats} className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full text-sm">
                      {seats} {t("common.seats", "seats")}
                      <button onClick={() => toggleSeats(seats)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {selectedColors.map((color) => (
                    <span key={color} className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-sm capitalize">
                      <span className="w-3 h-3 rounded-full border" style={{ backgroundColor: COLOR_OPTIONS.find(c => c.value === color)?.hex }} />
                      {color}
                      <button onClick={() => toggleColor(color)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  <button
                    onClick={clearFilters}
                    className="text-sm text-red-500 hover:text-red-600 font-medium px-2"
                  >
                    {t("filter.clearAll", "Clear All")}
                  </button>
                </div>
              )}

              {/* Cars Grid/List */}
              {loading ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="w-10 h-10 animate-spin text-[#D4AF37]" />
                </div>
              ) : cars.length > 0 ? (
                <div
                  className={viewMode === "grid" ? "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" : "space-y-4"}
                  data-testid="cars-grid"
                >
                  {cars.map((car) => (
                    <CarCard key={car.id} car={car} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-xl" data-testid="no-results">
                  <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Search className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="font-[var(--font-display)] text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                    {t("listings.noResults", "No Cars Found")}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-6">
                    {t("listings.noResultsHint", "Try adjusting your filters to find what you're looking for")}
                  </p>
                  <Button onClick={clearFilters} className="btn-gold text-black">
                    {t("filter.clearAll", "Clear All Filters")}
                  </Button>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default ListingsPage;
